// frontend/src/components/MassImportDialog.js
import React, { useState, useCallback } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Autocomplete,
    DialogContentText, Box, CircularProgress, IconButton, Paper, Snackbar, Alert, Typography,
    AppBar, Toolbar, useMediaQuery // Hinzugefügt
} from '@mui/material';
import { useTheme } from '@mui/material/styles'; // Hinzugefügt
import CloseIcon from '@mui/icons-material/Close';
import UploadFileIcon from '@mui/icons-material/UploadFile'; // Für Button Icon
import { LoadingButton } from '@mui/lab'; // Hinzugefügt

import { massImportBookAPI } from '../services/api';
import { useAuth } from '../hooks/useAuth';

function MassImportDialog({ open, onClose, locations, onImportComplete }) {
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [isbnList, setIsbnList] = useState('');
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState([]);
  const { handleApiError } = useAuth();
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('info');

  const theme = useTheme(); // Hinzugefügt
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // Hinzugefügt

  const handleLocationChange = useCallback((event, value) => {
    setSelectedLocation(value);
  }, []);

  const handleIsbnListChange = useCallback((event) => {
    setIsbnList(event.target.value);
  }, []);

  const showSnackbar = useCallback((message, severity = 'info') => {
      setSnackbarMessage(message);
      setSnackbarSeverity(severity);
      setSnackbarOpen(true);
  }, []);

  const handleImport = useCallback(async () => {
    if (!selectedLocation) {
      showSnackbar('Bitte wähle einen Lagerort aus.', 'warning');
      return;
    }
    setImporting(true);
    setImportResult([]);

    const isbns = isbnList.split(/[\n,;]+/)
                      .map(isbn => isbn.trim())
                      .filter(isbn => isbn !== '');

    if (isbns.length === 0) {
        showSnackbar('Bitte gib mindestens eine ISBN ein.', 'warning');
        setImporting(false);
        return;
    }

    const results = [];
    let successCount = 0;

    for (const isbn of isbns) {
      try {
        const data = await massImportBookAPI({ isbn, location: selectedLocation._id });
        results.push({ isbn, success: true, book: data });
        successCount++;
      } catch (error) {
        handleApiError(error);
        const message = error.status === 409 ? error.message : `Fehler: ${error.message || 'Unbekannt'}`;
        results.push({ isbn, error: message });
      }
      setImportResult([...results]); // Update progress
    }

    setImporting(false);
    showSnackbar(`${successCount} von ${isbns.length} Büchern erfolgreich importiert.`, successCount > 0 ? 'success' : 'info');

    if (successCount > 0 && onImportComplete) {
        onImportComplete();
    }
  }, [selectedLocation, isbnList, onImportComplete, handleApiError, showSnackbar]); // massImportBookAPI ist stabil

  const handleCloseDialog = useCallback(() => {
      setSelectedLocation(null);
      setIsbnList('');
      setImporting(false);
      setImportResult([]);
      onClose();
  }, [onClose]);

  const handleSnackbarClose = useCallback((event, reason) => {
      if (reason === 'clickaway') { return; }
      setSnackbarOpen(false);
  }, []);

  return (
    <Dialog open={open} onClose={handleCloseDialog} fullScreen={isMobile} fullWidth maxWidth="sm">
      {/* Mobile Header */}
      {isMobile && (
          <AppBar sx={{ position: 'relative' }}>
              <Toolbar>
                  <IconButton edge="start" color="inherit" onClick={handleCloseDialog} aria-label="close" disabled={importing}>
                      <CloseIcon />
                  </IconButton>
                  <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
                      Massenimport
                  </Typography>
                  <LoadingButton
                      color="inherit"
                      onClick={handleImport}
                      disabled={importing || !selectedLocation || !isbnList.trim()}
                      loading={importing}
                      loadingPosition="start"
                      startIcon={<UploadFileIcon />}
                  >
                      Importieren
                  </LoadingButton>
              </Toolbar>
          </AppBar>
      )}
      {/* Desktop Title */}
      {!isMobile && (
          <DialogTitle sx={{ m: 0, p: 2, bgcolor: 'primary.main', color: 'white' }}>
              Massenimport von Büchern
              <IconButton aria-label="close" onClick={handleCloseDialog} sx={{ position: 'absolute', right: 8, top: 8, color: 'white' }} disabled={importing}>
                  <CloseIcon />
              </IconButton>
          </DialogTitle>
      )}

      <DialogContent sx={isMobile ? { pt: 2 } : {}}>
        <Autocomplete
          id="location-select-mass"
          sx={{ width: '100%', mb: 2 }}
          options={locations}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) => option._id === value._id}
          value={selectedLocation}
          onChange={handleLocationChange}
          disabled={importing}
          renderInput={(params) => (
            <TextField {...params} label="Lagerort für alle Bücher" margin="dense" variant="outlined" required error={!selectedLocation && importResult.length > 0} />
          )}
        />
        <TextField
          label="ISBN-Liste (eine pro Zeile, Komma oder Semikolon)"
          multiline
          rows={isMobile ? 6 : 10} // Weniger Zeilen auf Mobile
          fullWidth
          margin="dense"
          variant="outlined"
          value={isbnList}
          onChange={handleIsbnListChange}
          disabled={importing}
        />
        {importing && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 2, gap: 1 }}>
            <CircularProgress size={20} />
            <Typography>Importiere Bücher ({importResult.length} / {isbnList.split(/[\n,;]+/).map(i=>i.trim()).filter(i=>i).length})...</Typography>
          </Box>
        )}
        {/* Ergebnisliste */}
        {importResult.length > 0 && !importing && (
          <Box mt={2} sx={{ maxHeight: '200px', overflowY: 'auto' }}>
            <Typography variant="subtitle2">Importergebnisse:</Typography>
            {importResult.map((result, index) => (
              <Paper key={index} elevation={1} sx={{ p: 1, mb: 1, bgcolor: result.error ? 'error.light' : 'success.light' }}>
                <DialogContentText sx={{ color: result.error ? 'error.dark' : 'success.dark', fontSize: '0.875rem' }}>
                  {result.error ? `ISBN ${result.isbn}: ${result.error}` : `ISBN ${result.isbn}: Erfolgreich importiert.`}
                </DialogContentText>
              </Paper>
            ))}
          </Box>
        )}
      </DialogContent>
      {/* Desktop Actions */}
      {!isMobile && (
          <DialogActions>
              <Button onClick={handleCloseDialog} disabled={importing}>Abbrechen</Button>
              <LoadingButton
                  onClick={handleImport}
                  disabled={importing || !selectedLocation || !isbnList.trim()}
                  variant="contained"
                  color="primary"
                  loading={importing}
                  loadingPosition="start"
                  startIcon={<UploadFileIcon />}
              >
                  {importing ? 'Import läuft...' : 'Importieren'}
              </LoadingButton>
          </DialogActions>
      )}

      {/* Snackbar */}
       <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleSnackbarClose}>
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Dialog>
  );
}

export default MassImportDialog;
